/**
 * @module app.shared
 */

import { bindingMode, autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { I18N } from 'aurelia-i18n';
import { TickService, TickHandler, ITickEvent } from 'app/shared/services/event-services';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { LockoutService } from 'app/shared/services/lockout-service';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { SyncService } from 'app/shared/services/sync-service';
import { AppSession } from 'app/shared/lib/session/session';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { SessionChangedEvent } from 'app/shared/lib/session/session-events';
import { PassphraseService } from 'app/shared/services/passphrase-service';

export enum LockoutState {
    Unlocked = 1,
    Warning = 2,
    Locked = 4
};

const DEBUG_PASSPHRASE = 'debug window status';

// Locks a user out of the application after a period of inactivity.
@autoinject
export class EssLockoutCustomElement {

    @bindable({ defaultBindingMode: bindingMode.oneTime }) warning: number; // warning time in minutes
    @bindable({ defaultBindingMode: bindingMode.oneTime }) lockout: number; // lockout time in minutes

    protected password = '';
    @bindable() protected state = LockoutState.Unlocked;
    protected isUnlocking = false;
    protected remainingSeconds = -1;
    protected lastActivity: number;
    protected resetLockoutTimer: () => void;
    protected checkInactivity: TickHandler;
    protected errorMessage: string = '';
    private subscriptions: Subscription[] = [];

    // refs
    protected $modal!: EssDialogCustomElement;

    protected LockoutState = LockoutState;

    constructor(
        private readonly events: EventAggregator,
        private readonly passphrase: PassphraseService,
        protected element: Element,
        protected session: AppSession,
        protected syncService: SyncService,
        protected lockService: LockoutService,
        protected ticker: TickService,
        protected i18n: I18N,
        protected formatProvider: FormatProvider,
        protected env: EnvironmentService) {

        // this needs to be defined on the instance, not on the prototype, in order to be cleaned up properly
        this.resetLockoutTimer = () => {
            this.lastActivity = Date.now();
            this.syncService.update(true);
            if(this.state === LockoutState.Warning) {
                this.state = LockoutState.Unlocked;
            }
        }

        this.checkInactivity = ({ time, elapsed }: ITickEvent) => {
            if(env.locked) {
                // if something else has locked the session (another window), react
                if(this.state !== LockoutState.Locked) {
                    this.lock(false);
                }
                this.debugWindowStatus(0);
                return;
            }
            const { lastActivity, warning, lockout } = this;
            const activeWindow = this.syncService.activeWindow;
            const lastSyncActivity = activeWindow ?
                Math.max(lastActivity, activeWindow && activeWindow.activity) : lastActivity;
            const remainingSeconds = (((lastSyncActivity + lockout * 60000) - time) / 1000) | 0; // truncate
            this.debugWindowStatus(remainingSeconds);

            const lockTime = time - (warning * 60000);
            if(remainingSeconds < 0) {
                // lock when timer hits zero
                if(this.syncService.isActive) {
                    // refresh from other windows
                    this.syncService.refresh();
                    // if still active, then we can lock
                    // this will make sure that a stale background window isn't acting on old information
                    if(this.syncService.isActive) {
                        // only the active windows needs to actually lock
                        // the others can respond accordingly
                        this.lock();
                    }
                }
            }
            else if(this.state === LockoutState.Unlocked && (lastSyncActivity < lockTime)) {
                // show warning
                this.state = LockoutState.Warning;
            }
            else if(this.state === LockoutState.Warning && (lastSyncActivity >= lockTime)) {
                // hide warning (in case activity is detected in another tab)
                this.state = LockoutState.Unlocked;
            }

            this.remainingSeconds = Math.max(0, remainingSeconds); // prevent '-0' from displaying

            // convert elapsed time from milliseconds to seconds (add 500 to support midpoint rounding)
            // update sync service
            this.syncService.update(false, ((elapsed + 500) / 1000) | 0);

            // last activity might have been updated elsewhere (transitional)
            const updatedActivity = this.syncService.currentWindow.activity;
            if(updatedActivity > this.lastActivity) {
                this.lastActivity = updatedActivity;
            }
        }

        // set the default value for the lockout timer, which will be overridden by the attribute binding if present
        this.warning = env.config.lockout.minutesToWarning;
        this.lockout = env.config.lockout.minutesToLockout;
        this.lastActivity = Date.now();
    }

    private get showDebug(): boolean {
        return !!(<any>window)['_ess_lock'];
    }

    private set showDebug(value: boolean) {
        (<any>window)['_ess_lock'] = value;
    }

    private debugWindowStatus(seconds: number): void {
        // TODO: clean up and formalize the "debug display" a bit for use elsewhere
        const debugId = 'debug-lockout-time';
        const showDebug = this.showDebug;
        let element = document.getElementById(debugId);

        if(element == null) {
            if(!showDebug) { return; }
            // create element on the fly
            element = document.createElement('pre');
            element.id = debugId;
            document.body.insertBefore(element, document.body.firstChild);
        }
        // toggle visibility
        element.style.display = showDebug ? 'block' : 'none';
        if(!showDebug) { return; }

        const text: string[] = [];
        const windows = this.syncService.allWindows;
        const activeId = this.syncService.activeWindow?.id || '';
        const currentId = this.syncService.currentWindow?.id || '';
        const now = Date.now();

        text.push(`Timeout: ${seconds}s, ${windows.length} Window(s)`);
        for(const window of windows) {
            const status: string[] = [];
            const { id, url, hidden, focused, closed, clockRate } = window;
            const updated = (now - window.updated) / 1000 | 0;
            const activity = (now - window.activity) / 1000 | 0;
            const current = id === currentId ? '=> ' : '   ';

            status.push(id === activeId ? '[active]' : '[inactive]');
            status.push(hidden ? '[hidden]' : '[visible]');
            if(focused) { status.push('[focused]'); }
            if(closed) { status.push('[closed]'); }

            const statusList = status.join(' ');
            text.push(`${current}${url} ${statusList}, updated ${updated}s ago, active ${activity}s ago, tick @ ${clockRate}s`);
        }

        element.innerText = text.join('\r\n');
    }

    protected stateChanged(newState: LockoutState, oldState: LockoutState): void {
        if(oldState !== LockoutState.Warning && newState === LockoutState.Warning) {
            this.$modal.open();
        }
        else if(oldState === LockoutState.Warning && newState !== LockoutState.Warning) {
            this.$modal.close();
        }
    }

    protected timeoutMessage(remainingSecondsDisplay: string) {
        const cutoff = 60 * 3;
        const timeRemaining = this.remainingSeconds < cutoff
            ? { value: remainingSecondsDisplay, type: 'seconds' }
            : { value: Math.round(this.remainingSeconds / 60), type: 'minutes' };
        return this.i18n.tr('ui:messages.lockedOutIn', {
            item: timeRemaining.value, item2: timeRemaining.type
        });
    }

    public lock(sendLock: boolean = true) {
        if(sendLock && !this.session.isSso) {
            this.lockService.lock(); // INFO [Matt] Fire and forget.
        }

        if(this.state !== LockoutState.Locked) {
            // set automatic password if that's the login we used
            const { loginName, password } = this.env.config.credentials;
            if(password && loginName === this.env.user.loginName) {
                this.password = password;
            }
            else {
                this.password = '';
            }
        }

        this.state = LockoutState.Locked;
        this.element.dispatchEvent(new CustomEvent('lock', { bubbles: true }));
    }

    public async unlock() {
        this.isUnlocking = true;
        if(this.session.isSso || await this.lockService.unlock(this.password)) {
            this.updateUnlockState();
        }
        else {
            this.errorMessage = 'Unable to Unlock Session.';
        }
        this.isUnlocking = false;
    }

    private updateUnlockState(): void {
        // this updates the state of this component to be unlocked
        // this can happen either by the user unlocking successfully
        // or when another window signals that it was unlocked
        this.state = LockoutState.Unlocked;
        this.element.dispatchEvent(new CustomEvent('unlock', { bubbles: true }));
        this.errorMessage = '';
        this.password = '';
    }

    private sessionChanged(event: SessionChangedEvent) {
        if(event.key === 'unlock') {
            this.updateUnlockState();
        }
    }

    protected attached() {
        this.errorMessage = '';
        document.addEventListener('mousemove', this.resetLockoutTimer);
        document.addEventListener('touchstart', this.resetLockoutTimer);
        document.addEventListener('keydown', this.resetLockoutTimer);
        document.addEventListener('wheel', this.resetLockoutTimer);
        window.addEventListener('focus', this.resetLockoutTimer);
        window.addEventListener('blur', this.resetLockoutTimer);
        this.ticker.addEventListener('tick', this.checkInactivity);

        // listen for session changed event
        this.subscriptions.push(
            this.events.subscribe(SessionChangedEvent, (e: SessionChangedEvent) => this.sessionChanged(e))
        );

        // listen for passphrase
        this.passphrase.registerPassphrase(DEBUG_PASSPHRASE, () => this.showDebug = !this.showDebug);
    }

    protected detached() {
        document.removeEventListener('mousemove', this.resetLockoutTimer);
        document.removeEventListener('touchstart', this.resetLockoutTimer);
        document.removeEventListener('keydown', this.resetLockoutTimer);
        document.removeEventListener('wheel', this.resetLockoutTimer);
        window.removeEventListener('focus', this.resetLockoutTimer);
        window.removeEventListener('blur', this.resetLockoutTimer);
        this.ticker.removeEventListener('tick', this.checkInactivity);

        // dispose subscriptions
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];

        // stop listening for passphrase
        this.passphrase.deregisterPassphrase(DEBUG_PASSPHRASE);
    }

}
